import {$qs, fetchHostWindowData} from '../../../@shared/ts/dom';
import {type InitMessage} from '../../../@type/window-messages';
import {type WCFieldsData} from '../../../@type/woocommerce/field-data';
import {getLocaleText} from '../util/translation';
import chevronDownImageURL from '../../img/chevron-down-solid.svg';

export function generateMainForm(message: InitMessage): HTMLElement {
	const form = document.createElement('form');
	form.id = 'pp-info-form';
	form.classList.add('pp-info-form', 'pp-info-form-new', 'flex', 'col');
	const container = document.createElement('div');
	container.classList.add('flex', 'col', 'pp-form-rows-container');
	container.appendChild(formGenerator(message.phpData?.billing_fields ?? [], message.phpData?.billing_fields_order ?? [], 'billing'));
	form.appendChild(container);
	return form;
}

export function generateShippingForm(message: InitMessage): HTMLDivElement {
	const container = document.createElement('div');
	container.classList.add('flex', 'col', 'pp-form-rows-container');
	container.appendChild(formGenerator(message.phpData?.shipping_fields ?? [], message.phpData?.shipping_fields_order ?? [], 'shipping'));
	return container;
}

export function initShippingForm() {
	$qs('#pp-different-shipping-details', $element => {
		$element.addEventListener('submit', (e: Event) => {
			e.preventDefault();
			return false;
		});
	});
}

function formGenerator(fieldData: WCFieldsData[], fieldOrder: number[], section: string, formIdDifferentiator = ''): HTMLDivElement {
	const formId = `pp-${formIdDifferentiator + (formIdDifferentiator ? '-' : '')}info-form`;
	// This creates 2 layers of container divs, which works but isn't clean. Here to "End block" should be standardized to just return the
	// flex container.
	let form = document.createElement('div');
	form.classList.add('flex', 'col', 'pp-form-rows-container');
	if (section === '') {
		const formForm = document.createElement('form');
		formForm.id = formId;
		formForm.classList.add('pp-info-form', 'flex', 'col');
		const formFormDiv = document.createElement('div');
		formFormDiv.classList.add('flex', 'col', 'pp-form-rows-container');
		formForm.appendChild(formFormDiv);
		form.appendChild(formForm);
		// Enables consistent referencing for adding children.
		form = formFormDiv;
	}
	// End block

	let i = 0;
	let rowSpace = 100;
	let rowElement = document.createElement('div');

	const labelNameTranslations: Record<string, string> = {
		Shipping: getLocaleText('Shipping'),
		Billing: getLocaleText('Billing'),
		Personal: getLocaleText('Personal'),
	};

	let field: WCFieldsData | undefined = fieldData[fieldOrder?.[i] ?? -1] ?? undefined;

	while (field) {
		const fieldWidth: number = parseInt(field.width);
		const label = labelNameTranslations[field.field_label] ?? field.field_label;
		if (field.type_list === 'header') {
			if (rowElement.innerHTML.length !== 0) {
				// Add a filler div to finish the row
				if (rowSpace !== 0) {
					const element = document.createElement('div');
					element.classList.add('pp-fw-' + rowSpace);
					rowElement.appendChild(element);
				}

				form.appendChild(rowElement);
				// Reset.
				rowSpace = 100;
				rowElement = document.createElement('div');
			}

			form.innerHTML += `<div id="${field.field_name}-field" class="${field.field_name}"><span class="pp-info-form-header">${label}</span></div>`;
			i++;
			field = fieldData[fieldOrder?.[i] ?? -1] ?? undefined;
			continue;
		}

		if (rowSpace < fieldWidth) {
			if (rowSpace !== 0) {
				const element = document.createElement('div');
				element.classList.add('pp-fw-' + rowSpace);
				rowElement.appendChild(element);
			}

			form.appendChild(rowElement);
			// Reset.
			rowSpace = 100;
			rowElement = document.createElement('div');
		}

		if (rowSpace >= fieldWidth) {
			if (rowElement.innerHTML.length === 0) {
				rowElement.classList.add('flex', 'pp-form-row');
			}

			rowElement.appendChild(generateFieldElement(field, section));
			rowSpace -= fieldWidth;
			i++;
			field = fieldData[fieldOrder?.[i] ?? -1] ?? undefined;
		}
	}

	if (rowElement.innerHTML.length !== 0) {
		if (rowSpace !== 0) {
			const element = document.createElement('div');
			element.classList.add('pp-fw-' + rowSpace);
			rowElement.appendChild(element);
		}

		form.appendChild(rowElement);
	}

	return form;
}

function generateFieldElement(fieldData: WCFieldsData, section: string): HTMLDivElement {
	const fieldWidth: number = parseInt(fieldData.width);
	// New
	const element = document.createElement('div');
	element.classList.add(fieldData.field_name + '-field', 'pp-fw-' + fieldWidth, fieldData.type_list === 'radio' ? 'flex-col' : 'flex');
	if (fieldData.type_list === 'checkbox') {
		element.style.alignItems = 'center';
	}

	const optional = fieldWidth <= 30 ? '<span class="optional"> (opt.) </span>' : '<span class="optional"> (optional) </span>';

	const labelNameTranslations: Record<string, string> = {
		'Street address': getLocaleText('Street address'),
		Apartment: getLocaleText('Apartment'),
		City: getLocaleText('City'),
		Country: getLocaleText('Country'),
		Email: getLocaleText('Email'),
		'First name': getLocaleText('First name'),
		'Last name': getLocaleText('Last name'),
		'Phone number': getLocaleText('Phone number'),
		'Postal code': getLocaleText('Postal code'),
		State: getLocaleText('State'),
		Province: getLocaleText('Province'),
	};

	const label = labelNameTranslations[fieldData.field_label] ?? fieldData.field_label;

	const labelBuilder = () =>
		`<label title="${label}" for="${fieldData.field_name}"
		class="${(fieldData.type_list === 'checkbox' || fieldData.type_list === 'radio') ? 'pp-alt-label' : 'pp-form-label'}
		${fieldData.type_list === 'select' ? ' pp-select-label' : ''}">
			${getLimitedLabel(fieldData, label)}
			${(fieldData.field_required !== 'yes' && !['Apartment', 'Apt.'].includes(fieldData.field_label)) ? optional : ''}
		</label>${(fieldData.type_list === 'select') ? `<div class="pp-form-chevron"><img src="${chevronDownImageURL}"/></div>` : ''}`;

	const inputBuilder = () =>
		`<input type="${fieldData.type_list}"
			name="${fieldData.field_name}"
			placeholder=" "`
		+ (fieldData.field_default ? `value="${fieldData.field_default.replace(/"/g, '&quot;')}"` : '')
		+ 'class="pp-fw-100 text-input"'
		+ (fieldData.field_required === 'yes' ? ' required' : '')
		+ '/>';

	const selectBuilder = (optionOrder: string[][]) =>
		`<select name=${fieldData.field_name}
			class="pp-fw-100"
			${fieldData.field_required === 'yes' ? 'required' : ''}>
				${optionBuilder(optionOrder)}
		</select>
		${labelBuilder()}`;

	const optionBuilder = (optionOrder: string[][]) => {
		if (optionOrder.length === 0) {
			return '';
		}

		let optionList = '<option value="">Please Select</option>';
		optionOrder.forEach(value => {
			if (value[0] && value[1]) {
				optionList += `<option value="${value[0]}">${value[1].replace(/\\"/g, '"').replace(/\\'/g, '\'')}</option>`;
			}
		});
		return optionList;
	};

	const radioFieldBuilder = (optionOrder: string[][]) => {
		let radioFields = '<div class="pp-radio-field">';
		radioFields += fieldData.field_label ? labelBuilder() : '';
		radioFields += '<div class="pp-radio-option-container">';
		let first = true;
		optionOrder.forEach(value => {
			if (value[0] && value[1]) {
				radioFields += `<label class="pp-radio-line"><input type=${fieldData.type_list}
				name="${fieldData.field_name}"`
					+ `value="${value[0]}"`
					+ 'class="pp-radio-input"'
					+ (fieldData.field_required === 'yes' && first ? ' required' : '')
					+ (fieldData.field_default === value[0] ? ' checked' : '')
					+ '/>';
				radioFields += `<span class="radio-option-label">${value[1].replace(/\\"/g, '"').replace(/\\'/g, '\'')}</span></label>`;
				if (first) {
					first = false;
				}
			}
		});
		radioFields += '</div></div>';
		return radioFields;
	};

	const checkbox = () => `
	<div class="pp-checkbox-input-container">
		<input
			type="checkbox"
			class="pp-form-checkbox"
			name="${fieldData.field_name}"
			value="${fieldData.field_default ? fieldData.field_default : '1'}"
			style="float: right;"
			${fieldData.field_required === 'yes' ? 'required' : ''}
		>
	</div>
	${labelBuilder()}
	`;

	const stateRegionBuilder = (section: string) => `
		<input name="${section}_province" class="pp-fw-100 text-input" type="text" name="off" placeholder=" ">
		<label for="${section}_province" class="pp-form-label">${labelNameTranslations['Province'] + optional}</label>
		<select name="${fieldData.field_name}" class="pp-fw-100 select hide" size="1">
			<option hidden disabled selected value></option>
		</select>
		<label for="${fieldData.field_name}" class="pp-form-label pp-select-label hide"></label>
		<div class="pp-form-chevron"><img src="${chevronDownImageURL}"/></div>
		`;

	const countryFieldBuilder = () => `
		<select class="w-100" name="${fieldData.field_name}" size="1" ${(fieldData.field_required === 'yes' ? 'required' : '')}>
			<option hidden disabled selected>
				${getLocaleText('Select a country')}
			</option>
		</select>
		<label for="${fieldData.field_name}" class="pp-form-label pp-select-label">
			${getLocaleText('Country')}
		</label>
		<div class="pp-form-chevron"><img src="${chevronDownImageURL}"/></div>
	`;

	const passwordInputBuilder = () =>
		`<input type="${fieldData.type_list}"
			name="${fieldData.field_name}"
			placeholder=" "
			minlength="8"`
		+ (fieldData.field_default ? `value="${fieldData.field_default.replace(/"/g, '&quot;')}"` : '')
		+ 'class="pp-fw-100 text-input"'
		+ (fieldData.field_required === 'yes' ? ' required' : '')
		+ '/>';

	if (fieldData.type_list === 'text' || fieldData.type_list === 'email' || fieldData.type_list === 'tel') {
		element.innerHTML = inputBuilder() + (fieldData.field_label ? labelBuilder() : '');
		if (fieldData.type_list === 'email') {
			// Email grabbing for abandoned cart analytics.
			element.addEventListener('focusout', updateEmail);
		}
	} else if (fieldData.type_list === 'select') {
		element.innerHTML = selectBuilder(fieldData.option_order ?? []);
	} else if (fieldData.type_list === 'radio') {
		element.innerHTML = radioFieldBuilder(fieldData.option_order ?? []);
	} else if (fieldData.type_list === 'checkbox') {
		element.innerHTML = checkbox();
	} else if (fieldData.type_list === 'country') {
		element.innerHTML = countryFieldBuilder();
	} else if (fieldData.type_list === 'state') {
		element.innerHTML = stateRegionBuilder(section);
	} else if (fieldData.type_list === 'password') {
		element.innerHTML = passwordInputBuilder() + (fieldData.field_label ? labelBuilder() : '');
	}

	return element;
}

/**
 * Takes a fieldData and returns its label, truncated based on its width so that the label doesn't overflow.
 * An ellipsis is appended as needed.
 * @param fieldData
 * @returns truncated label
 */
function getLimitedLabel(fieldData: WCFieldsData, label: string): string {
	const fieldWidth: number = parseInt(fieldData.width);
	if ((fieldWidth === 100) && (fieldData.type_list === 'radio')) {
		return label;
	}

	try {
		// Since apt. number is universally optional, make sure it isn't required
		const isOptional = fieldData.field_required !== 'yes' && !['Apartment', 'Apt.'].includes(fieldData.field_label);
		let optionalTagWidth = 0;
		if (isOptional) {
			optionalTagWidth = fieldWidth <= 30 ? 40 : 68;
		}

		let maxWidth = 0;
		if (fieldWidth <= 30) {
			maxWidth = 75;
		} else if (fieldWidth <= 50) {
			maxWidth = 164;
		} else if (fieldWidth <= 70) {
			maxWidth = 250;
		} else {
			maxWidth = 378;
		}

		const $canvas: HTMLCanvasElement = document.createElement('canvas');
		const ctx: CanvasRenderingContext2D | null = $canvas?.getContext('2d');
		if (!ctx) {
			$canvas.remove();
			return label;
		}

		// Poor font flexibility, but this is the best that can be done before rendering.
		const font
				= '14.4px Inter, -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Noto Sans\', Ubuntu, Cantarell, \'Helvetica Neue\', sans-serif, \'Apple Color Emoji\', \'Segoe UI Emoji\', \'Segoe UI Symbol\', \'Noto Color Emoji\'';
		ctx.font = font;

		const tooLong = () => ctx.measureText(label).width + optionalTagWidth > maxWidth;

		if (tooLong()) {
			while (tooLong()) {
				label = label.slice(0, label.length - 1);
			}

			label += '…';
		}

		$canvas.remove();

		return label;
	} catch {
		return label;
	}
}

async function updateEmail(e: Event) {
	const email = (e.target as HTMLInputElement).value;
	await fetchHostWindowData('pp-update-email', {
		email,
	});
}
